import { Box, Button, HStack, IconButton, Link, Text } from '@chakra-ui/react';
import { useMemo, useState, type ReactNode } from 'react';
import { FiPlus } from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';

import { documentViewHref } from '#utils/document-app-path';
import { formatDocumentName } from '#utils/format-document-name';

import {
  SubpagesLinksList,
  type SubpagesLinksListProps,
} from './subpages-links-list';

export interface DocumentTitleSubpagesPortalProps
  extends SubpagesLinksListProps {
  onCreateSubpage?: () => void;
  /** Match editor placeholder: soften path until the user edits a new path. */
  editorPlaceholderMuted?: boolean;
  isNewDocument: boolean;
  onRequestRename?: () => void;
}

const crumbFontSize = '13px';
const crumbLineHeight = '1.35';

/** Path line, subpage toggle, create control, and portaled subpage links under the document heading. */
export function DocumentTitleSubpagesPortal({
  parentPath,
  children: childDocs,
  onCreateSubpage,
  onNavigateToDocument,
  editorPlaceholderMuted = false,
  isNewDocument,
  onRequestRename,
}: DocumentTitleSubpagesPortalProps) {
  const [subpagesExpanded, setSubpagesExpanded] = useState(true);
  const hasChildren = childDocs.length > 0;

  const segments = useMemo(() => {
    const p = parentPath.trim();
    if (!p.length) return [];

    return p.split('/').filter((part) => part.length > 0);
  }, [parentPath]);

  const formattedLabels = useMemo(
    () => formatDocumentName(parentPath.trim()),
    [parentPath],
  );

  const pathColor = editorPlaceholderMuted
    ? 'var(--text-muted)'
    : 'var(--font-base-color)';

  const sep = (key: string) => (
    <Text
      key={key}
      as="span"
      aria-hidden
      flexShrink={0}
      fontSize={crumbFontSize}
      lineHeight={crumbLineHeight}
      color="var(--text-muted)"
      px={1.5}
    >
      ›
    </Text>
  );

  const breadcrumbNodes = useMemo(() => {
    if (segments.length === 0) return [];

    const nodes: ReactNode[] = [];
    const n = segments.length;

    for (let i = 0; i < n; i += 1) {
      const label = formattedLabels[i] ?? segments[i];
      const cumulative = segments.slice(0, i + 1).join('/');
      const isLeaf = i === n - 1;

      if (i > 0) {
        nodes.push(
          <Text
            key={`sep-${i}`}
            as="span"
            aria-hidden
            flexShrink={0}
            fontSize={crumbFontSize}
            lineHeight={crumbLineHeight}
            color="var(--text-muted)"
            px={1.5}
          >
            ›
          </Text>,
        );
      }

      if (!isLeaf) {
        nodes.push(
          <Link
            key={`link-${cumulative}`}
            asChild
            display="inline"
            fontSize={crumbFontSize}
            lineHeight={crumbLineHeight}
            color="var(--primary)"
            fontWeight="normal"
            _hover={{ textDecoration: 'underline' }}
          >
            <RouterLink to={documentViewHref(cumulative)}>{label}</RouterLink>
          </Link>,
        );
      } else if (!isNewDocument && typeof onRequestRename === 'function') {
        nodes.push(
          <Button
            key="leaf-rename"
            type="button"
            variant="ghost"
            size="xs"
            h="auto"
            minH={0}
            py={0}
            px={1}
            fontWeight="normal"
            fontSize={crumbFontSize}
            lineHeight={crumbLineHeight}
            color={pathColor}
            flexShrink={0}
            aria-label="Rename document"
            title="Rename document"
            onClick={() => onRequestRename()}
          >
            {label}
          </Button>,
        );
      } else {
        nodes.push(
          <Text
            key="leaf-text"
            as="span"
            display="inline"
            fontSize={crumbFontSize}
            lineHeight={crumbLineHeight}
            color={pathColor}
          >
            {label}
          </Text>,
        );
      }
    }

    return nodes;
  }, [segments, formattedLabels, isNewDocument, onRequestRename, pathColor]);

  return (
    <Box mt={1}>
      <HStack
        className="markdown-document-title__breadcrumb-row"
        gap={0}
        align="center"
        flexWrap="wrap"
        justify="flex-start"
        aria-label="Document path"
      >
        {breadcrumbNodes}
        {hasChildren && (
          <>
            {segments.length > 0 ? sep('sep-before-hide') : null}
            <Button
              type="button"
              variant="ghost"
              size="xs"
              h="auto"
              minH={0}
              py={0}
              px={1}
              fontWeight="normal"
              color="var(--text-muted)"
              fontSize={crumbFontSize}
              lineHeight={crumbLineHeight}
              aria-expanded={subpagesExpanded}
              flexShrink={0}
              onClick={() => setSubpagesExpanded((v) => !v)}
            >
              {subpagesExpanded ? 'Hide' : '...Show All'}
            </Button>
          </>
        )}
        {segments.length > 0 || hasChildren ? sep('sep-before-plus') : null}
        <IconButton
          type="button"
          aria-label="Create subpage"
          variant="ghost"
          size="sm"
          flexShrink={0}
          disabled={typeof onCreateSubpage !== 'function'}
          onClick={onCreateSubpage}
        >
          <FiPlus />
        </IconButton>
      </HStack>

      {hasChildren && subpagesExpanded && (
        <Box mt={1}>
          <SubpagesLinksList
            parentPath={parentPath}
            children={childDocs}
            onNavigateToDocument={onNavigateToDocument}
          />
        </Box>
      )}
    </Box>
  );
}
